import { AliasType, EntityFilterInput, PaginationSort } from '../gql/graphql';
import {
  getFirstPaginatedNode,
  getPaginatedNodes,
  toPagination,
} from './utils';
import { graphql } from '../gql';
import { convergeLogger } from '@converge/config';
import moment from 'moment';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_BROKERAGE_LIST = graphql(`
  query getBrokerageList(
    $pagination: PaginationRequest
    $filters: EntityFilterInput!
    $sort: PaginationSort
    $includeDetailed: Boolean = true
    $includePeople: Boolean = true
    $peoplePag: PaginationRequest = { after: 0, first: 3000 }
  ) {
    entities(pagination: $pagination, filters: $filters, sort: $sort) {
      edges {
        cursor
        node {
          uuid
          referenceId
          type
          status
          agency
          createdAt
          aliases(
            filters: { source: PRIMARY, type: LEGAL_NAME }
            pagination: { first: 1 }
          ) {
            edges {
              cursor
              queryCursor
              node {
                uuid
                name
                type
                sources
                primary
              }
            }
          }
          uwTerritories @include(if: $includeDetailed) {
            edges {
              node {
                uuid
                name
              }
            }
          }
          offices @include(if: $includeDetailed) {
            pageInfo {
              totalResults
            }
          }
          parents(filters: { depthSearch: 1 }) @include(if: $includeDetailed) {
            edges {
              node {
                uuid
                depthLevel
                referenceId
                aliases {
                  edges {
                    cursor
                    queryCursor
                    node {
                      uuid
                      name
                      type
                      sources
                      primary
                    }
                  }
                }
              }
            }
          }
          partner {
            edges {
              node {
                uuid
                depthLevel
                referenceId
                aliases {
                  edges {
                    cursor
                    queryCursor
                    node {
                      uuid
                      name
                      type
                      sources
                      primary
                    }
                  }
                }
              }
            }
          }

          persons(
            pagination: $peoplePag
            filters: { active: true, status: ACTIVE }
          ) @include(if: $includePeople) {
            pageInfo {
              totalResults
            }
          }
          teams @include(if: $includeDetailed) {
            pageInfo {
              totalResults
            }
          }
          addresses(filters: { source: PRIMARY }) {
            edges {
              cursor
              node {
                uuid
                street1
                street2
                city
                type
                postalCode
                source
                regions {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                countries {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                primary
              }
            }
          }
        }
      }
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
    }
  }
`);
export function useGetEntitiesBrokerages({
  pageNumber = 0,
  pageSize = 6,
  uuid,
  isAgency = true,
  isParent = undefined,
  sort,
  filters,
}: {
  pageNumber?: number;
  pageSize?: number;
  uuid?: string;
  isAgency?: boolean;
  isParent?: boolean;
  sort?: PaginationSort;
  filters?: EntityFilterInput;
}) {
  const variables = {
    pagination: toPagination(pageNumber, pageSize),
    filters: {},
    sort,
  };
  if (isParent !== undefined) {
    variables.filters = {
      agency: isAgency,
      parents: isParent,
      ...filters,
    };
  } else {
    variables.filters = {
      agency: isAgency,
      ...filters,
    };
  }
  const { loading, error, data } = useQuery(GET_BROKERAGE_LIST, {
    variables,
    fetchPolicy: 'no-cache',
  });

  const brokerages = useMemo(() => {
    if (!data || !data.entities || !data.entities.edges) return [];

    return getPaginatedNodes(data.entities)
      .filter((e) => e.agency)
      .map((edge) => {
        const alias = getPaginatedNodes(edge.aliases).find(
          ({ type, primary }) => primary
        );

        const parentNode = getFirstPaginatedNode(edge?.parents);
        const parentAlias = getPaginatedNodes(parentNode?.aliases).find(
          ({ type, primary }) => type === AliasType.LegalName && primary
        );

        const partnerNode = getFirstPaginatedNode(edge?.partner);
        const partnerAlias = getPaginatedNodes(partnerNode?.aliases).find(
          ({ type, primary }) => type === AliasType.LegalName && primary
        );
        if (!alias) {
          convergeLogger.error(
            `No primary legal name alias found for broker ${edge.uuid}`
          );
        }

        const primaryAddress = edge.addresses.edges.find((item) => {
          const node = item.node;
          return node?.type?.includes('CORPORATE');
        });

        return {
          value: edge.uuid,
          label: (alias && alias.name) ?? edge.referenceId ?? '\u2014',
          id: edge?.uuid,
          brokerage: (alias && alias.name) ?? edge.referenceId ?? '\u2014',
          primaryAddress: getFirstPaginatedNode(edge?.addresses),
          city: primaryAddress?.node.city ?? '\u2014',
          state:
            primaryAddress?.node?.regions?.edges[0]?.node?.name ?? '\u2014',
          uwRegion: edge?.uwTerritories?.edges[0]?.node?.name ?? '\u2014',
          brokers: edge?.persons?.pageInfo?.totalResults,
          offices: edge?.offices?.pageInfo?.totalResults,
          teams: edge?.teams?.pageInfo?.totalResults,
          onboarded: moment(edge?.createdAt?.split('T')[0]).format(
            'MM/DD/YYYY'
          ),
          status: edge?.status ?? '\u2014',
          parentEntity: parentAlias?.name ?? '\u2014',
          parentEntityUuid: parentNode?.uuid,
          partnerEntity: partnerAlias?.name ?? '\u2014',
          partnerEntityUuid: partnerNode?.uuid,
        };
      });
  }, [data]);

  return {
    error,
    loading,
    data,
    brokerages,
  };
}
